import { ChangeDetectorRef, Component, ErrorHandler, OnDestroy, OnInit, ChangeDetectionStrategy, Injector, inject, signal } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { ActivatedRoute, Router } from '@angular/router'

import { LaskuComponentDataResolve, LaskuComponentExistingData, LaskuKatseleComponentData } from '../_angular/_resolvers/lasku.resolve'
import { AsiakasService, AsiakkaanAvainTiedot } from '../_angular/service/asiakas/asiakas.service'
import { KirjanpitajaService } from '../_angular/service/kirjanpitaja/kirjanpitaja.service'
import { LaskuService } from '../_angular/service/lasku/lasku.service'
import { LEMONATOR_CF_API, LemonHttpService } from '../_angular/service/lemon-http.service'

import { Asiakas } from '../_jaettu-lemonator/model/asiakas'

import {
  EmailLahetysStatus,
  EmailLahetysStatusKoodi,
  Lasku,
  LaskuBase,
  LaskuReskontra,
  LaskuSahkoinenLahetysStatusKoodi,
  LaskuToimintaloki,
  Laskuasetukset,
  LaskunLiitetiedosto,
  LaskunTila,
  LaskunToimintalokiSahkoinenParametrit,
  LaskunToimintalokiSpostiParametrit,
  LaskunToimintalokiTilastoParametrit,
  LaskunToimintalokiTyyppi,
  LaskunumeroTyyppi,
  LaskutettavatPalvelut,
  TilastoTiedot,
} from '@lemon/core-firebase/model/lasku'

import { LaskuPdfEsikatselutiedot } from '../_jaettu-angular/laskut/esikatselu/pdf.perinteinen.component'
import { LadataanService } from '../_jaettu-angular/service/ladataan.service'
import { LemonTranslationService } from '../_jaettu-angular/service/lemon-translation.service'
import { TimestampService } from '../_jaettu-angular/service/timestamp-service'
import { LaskuReskontraTiedotDialog } from './dialogit/lasku.reskontra-tiedot.dialog'

import { BehaviorSubject, Observable, Subject, combineLatest, firstValueFrom, of } from 'rxjs'
import { map, startWith, switchMap, take, takeUntil } from 'rxjs/operators'

import { LaskuKokoVirheDialog } from './dialogit/lasku.koko-virhe.dialog'
import { LaskuLataaDialog, LaskuLataaDialogData } from './dialogit/lasku.lataa.dialog'

import { LaskuSharedService, ReskontraService } from '@lemon/core-firebase/service/lasku/lasku-shared.service'
import { LaskuUriService } from '@lemon/core-firebase/service/lasku/lasku-uri.service'
import { SahkoisenLaskunValittajaService, Verkkolaskuoperaattori } from '@lemon/core-firebase/service/lasku/sahkoisen-laskun-valittaja.service'
import { CurrencyService } from '@lemon/core/service/currency.service'
import { DateService } from '@lemon/core/service/date.service'

import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms'
import { applyEach, email, FieldTree, form, required } from '@angular/forms/signals'
import { MatSnackBar } from '@angular/material/snack-bar'
import { KirjanpitajanRooli } from '@lemon/core-firebase/lemonator/model/kirjanpitaja'
import { LaskuKopioija } from '@lemon/core-firebase/service/lasku/lasku.kopioija'
import { TuettuKieli } from '@lemon/core/model/common'
import { StringService } from '@lemon/core/service/string.service'
import { DebugService } from 'app/_angular/service/debug.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { arvonMuutokset, asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { validoiReaktiivisesti } from 'app/_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'
import { lemonShare } from 'app/_jaettu-angular/_rxjs/lemon-share.operator'
import { FileSaverService } from 'app/_jaettu-angular/service/file-saver'
import { FormValidationService } from 'app/_jaettu-angular/service/form-validation.service'
import { VasenValikkoService } from 'app/_jaettu-angular/service/vasen-valikko.service'
import { KirjanpitajanNimitiedot, KirjanpitajanTiimi } from 'app/_jaettu-lemonator/model/kirjanpitaja'
import { KirjanpitajanTiimiService } from 'app/_jaettu-lemonator/service/kirjanpitajan-tiimi.service'
import { KirjanpitoUriService } from 'app/_jaettu-lemonator/service/kirjanpito-uri.service'

interface KasiteltyLokitieto {
  parametrit: any
  loki: LaskuToimintaloki
  tekija: string
  viesti: string
}

interface TilastoLomake {
  kirjanpitaja: KirjanpitajanNimitiedot | null
  tiimi: KirjanpitajanTiimi | null
}

interface SahkoinenLomake {
  ytunnus: string
  osoite: string
  valittaja: string | null
}

interface KorjattavaEmailLahetysStatus extends EmailLahetysStatus {
  vanhaEmail: string
}

interface TiimiJaNimi {
  tiimi: KirjanpitajanTiimi
  nimi: string
}

@Component({
  templateUrl: './lasku.katsele.component.html',
  styleUrls: ['./lasku.katsele.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LaskuKatseleComponent implements OnInit, OnDestroy {
  private ngUnsubscribe = new Subject<void>()
  private readonly _injector = inject(Injector)

  reskontraLokiObservable: Observable<{ teksti: string; data: LaskuReskontra }[]>
  reskontraSummaObservable: Observable<number>
  reskontraaLadataanObservable: Observable<boolean>
  lokitiedotObservable: Observable<KasiteltyLokitieto[]>
  lokitietojaLadataanObservable: Observable<boolean>
  laskuUriObservable: Observable<string>
  laskuEncodedUriObservable: Observable<string>
  kirjattavakuittiUriObservable: Observable<string>
  kirjattavakuittiEncodedUriObservable: Observable<string>
  tilastoUriObservable: Observable<string>
  tilastoEncodedUriObservable: Observable<string>
  kirjanpitajaOnDevaajaObservable: Observable<boolean> = this._kirjautunutKayttajaService.kirjanpitajaOnDevaajaObservable
  kirjanpitajaOnDevaajaTaiTukiObservable = this._kirjautunutKayttajaService.kirjanpitajaOnDevaajaTaiTukiObservable
  tilastoKirjanpitajaTaiSuperObservable: Observable<boolean>
  tiimitJaNimetObservable: Observable<TiimiJaNimi[]>
  kirjanpitajienNimiTiedotObservable: Observable<KirjanpitajanNimitiedot[]>
  tilastotiedotObservable: BehaviorSubject<{ nimi: string; tiimi: string }> = new BehaviorSubject({ nimi: '', tiimi: '' })
  naytaKampanjahyvitysInfoObservable: Observable<boolean>

  verkkolaskuoperaattorit: Verkkolaskuoperaattori[]

  lokit: LaskuToimintaloki[] = []
  data: LaskuKatseleComponentData = null
  juurilasku: Lasku = null
  kasiteltava: LaskuBase = null
  asiakas: Asiakas = null
  asetukset: Laskuasetukset = null
  ladaataanToimintalokia = true
  esikatselutiedot: LaskuPdfEsikatselutiedot = null
  otsikko: string = null
  namename = 'asfrtegoq' + Math.random()

  sahkoinenLomake: FieldTree<SahkoinenLomake>
  tilastoLomake: FieldTree<TilastoLomake>
  vastaanottajienLomake = form(signal<string[]>([]), polku => {
    applyEach(polku, osoite => {
      required(osoite)
      email(osoite)
    })
  })

  private _buttonPushInflight: boolean = false

  annaOsoitteenValidointiFunktio(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value) {
        const ctrlValue = this._stringService.removeAllWhiteSpaces(control.value)
        const valittajat = this.verkkolaskuoperaattorit
        for (const valittaja of valittajat) {
          if (this._stringService.removeAllWhiteSpaces(valittaja.tunnus) === ctrlValue) {
            return { valittaja: true }
          }
        }
      }
      return null
    }
  }

  constructor(
    private _router: Router,
    private _route: ActivatedRoute,
    private _changeDetectorRef: ChangeDetectorRef,
    private _laskuService: LaskuService,
    private _errorHandler: ErrorHandler,
    public _laskuSharedService: LaskuSharedService,
    private _kirjanpitoUriService: KirjanpitoUriService,
    private _dialog: MatDialog,
    private _laskuComponentDataResolve: LaskuComponentDataResolve,
    private _dateService: DateService,
    private _httpService: LemonHttpService,
    private _lemonTranslationService: LemonTranslationService,
    private _currencyService: CurrencyService,
    private _timestampService: TimestampService,
    private _ladataanService: LadataanService,
    private _reskontraService: ReskontraService,
    private _asiakasService: AsiakasService,
    private _kirjanpitajaService: KirjanpitajaService,
    private _laskuUriService: LaskuUriService,
    private _sahkoisenLaskunValittajaService: SahkoisenLaskunValittajaService,
    private _stringService: StringService,
    private _validationService: FormValidationService,
    private _debugService: DebugService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _fileSaverService: FileSaverService,
    private _kirjanpitajanTiimiService: KirjanpitajanTiimiService,
    private _snackbar: MatSnackBar,
    private _vasenValikkoService: VasenValikkoService,
    private _laskuKopioija: LaskuKopioija,
  ) {}

  // private _filter(value): Verkkolaskuoperaattori[] {
  //   if (!value) {
  //     return []
  //   }
  //   const tarkasteltavaArvo = (value + '').trim().toLowerCase()
  //   if (tarkasteltavaArvo === '') {
  //     return []
  //   }
  //   return this.sahkoisenLaskunValittajaService.annaKaikkiValittajat().filter(operaattori => operaattori.nimi.toLowerCase().includes(tarkasteltavaArvo) || operaattori.tunnus.toLowerCase().includes(tarkasteltavaArvo))
  // }

  ngOnInit() {
    this.laskuUriObservable = combineLatest([this._asiakasService.nykyinenAsiakasAvainObservable, this._route.data]).pipe(
      map(([asiakas, data]: [Asiakas, { data: LaskuKatseleComponentData }]) => {
        if (asiakas && data?.data?.juurilasku) {
          return this._laskuUriService.getCustomerLaskuUri(this._laskuService.LEMONTREE_ASIAKAS_AVAIN, data.data.juurilasku.avain)
        }
        return ''
      }),
    )

    this.laskuEncodedUriObservable = combineLatest([this._asiakasService.nykyinenAsiakasAvainObservable, this._route.data]).pipe(
      map(([asiakas, data]: [Asiakas, { data: LaskuKatseleComponentData }]) => {
        if (asiakas && data?.data?.juurilasku) {
          const uri = this._laskuUriService.getCustomerLaskuUri(this._laskuService.LEMONTREE_ASIAKAS_AVAIN, data.data.juurilasku.avain)
          return this._debugService.createFirestoreLinkLemonaid(uri)
        }
        return ''
      }),
    )

    this.kirjattavakuittiUriObservable = combineLatest([this._asiakasService.nykyinenAsiakasAvainObservable, this._route.data]).pipe(
      map(([asiakas, data]: [Asiakas, { data: LaskuKatseleComponentData }]) => {
        if (asiakas && data?.data?.juurilasku) {
          return this._kirjanpitoUriService.annaKirjattavanFirestoreTositteenUriAvaimella(asiakas.avain, data.data.juurilasku.avain)
        }
        return ''
      }),
    )
    this.kirjattavakuittiEncodedUriObservable = combineLatest([this._asiakasService.nykyinenAsiakasAvainObservable, this._route.data]).pipe(
      map(([asiakas, data]: [Asiakas, { data: LaskuKatseleComponentData }]) => {
        if (asiakas && data?.data?.juurilasku) {
          const kirjattavaKuittiUri = this._kirjanpitoUriService.annaKirjattavanFirestoreTositteenUriAvaimella(asiakas.avain, data.data.juurilasku.avain)
          return this._debugService.createFirestoreLink(kirjattavaKuittiUri)
        }
        return ''
      }),
    )

    this.tilastoLomake = form(
      signal<TilastoLomake>({ kirjanpitaja: null, tiimi: null }),
      polku => {
        required(polku.kirjanpitaja)
        required(polku.tiimi)
      },
      { injector: this._injector },
    )

    this.sahkoinenLomake = form(
      signal<SahkoinenLomake>({ ytunnus: '', osoite: '', valittaja: '' }),
      polku => {
        required(polku.osoite)
        validoiReaktiivisesti(polku.osoite, this.annaOsoitteenValidointiFunktio())
        required(polku.valittaja)
      },
      { injector: this._injector },
    )

    this.verkkolaskuoperaattorit = this._sahkoisenLaskunValittajaService.annaKaikkiValittajat()

    // this.sahkoinenForm.get('ytunnus').valueChanges.subscribe(value => {
    //   this.sahkoinen.ytunnus = value
    // })
    arvonMuutokset(this.sahkoinenLomake.osoite, { injector: this._injector }).subscribe(value => {
      this.kasiteltava.sahkoinen.osoite.sahkoinenOsoite = value?.trim()
    })
    arvonMuutokset(this.sahkoinenLomake.valittaja, { injector: this._injector }).subscribe(value => {
      this.kasiteltava.sahkoinen.osoite.sahkoinenValittaja = value?.trim()
    })
    arvonMuutokset(this.vastaanottajienLomake, { injector: this._injector })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(osoitteet => {
        osoitteet.forEach((osoite, i) => {
          const vastaanottaja = this.kasiteltava?.email?.vastaanottajat?.[i]
          if (vastaanottaja && (vastaanottaja.email ?? '') !== osoite) {
            vastaanottaja.email = osoite
          }
        })
      })

    combineLatest([this._route.data, this._asiakasService.nykyinenAsiakasObservable])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(async ([data, asiakas]: [{ data: LaskuKatseleComponentData }, Asiakas]) => {
        this.asiakas = asiakas

        this.juurilasku = data.data.juurilasku
        this.kasiteltava = data.data.kasiteltava
        asetaArvo(
          this.vastaanottajienLomake,
          (this.kasiteltava.email?.vastaanottajat ?? []).map(vastaanottaja => vastaanottaja.email ?? ''),
          { ilmoita: false },
        )
        this.asetukset = data.data.asetukset
        this.data = data.data
        if (this.kasiteltava.email && this.kasiteltava.email.vastaanottajat) {
          for (const vastaanottaja of this.kasiteltava.email.vastaanottajat) {
            const korjattava = vastaanottaja as KorjattavaEmailLahetysStatus
            korjattava.vanhaEmail = korjattava.email
          }
        }
        if (this.kasiteltava.sahkoinen && this.kasiteltava.sahkoinen.osoite) {
          const valittajaTunnus = this.kasiteltava.sahkoinen.osoite.sahkoinenValittaja
          asetaArvo(this.sahkoinenLomake.osoite, this.kasiteltava.sahkoinen.osoite.sahkoinenOsoite ?? '')
          // This form field used to be a free-text input, so an extra comparison is needed here to catch invalid operator ID-s in legacy data.
          const haettuValittaja: Verkkolaskuoperaattori = this._sahkoisenLaskunValittajaService.annaValittaja(valittajaTunnus)
          const confirmedValittajaTunnus = haettuValittaja?.tunnus ?? null
          asetaArvo(this.sahkoinenLomake.valittaja, confirmedValittajaTunnus)
        }
        this.esikatselutiedot = {
          asetukset: this.asetukset,
          juurilasku: this.juurilasku,
          kasiteltava: this.kasiteltava,
        }
        if (this.naytaKorvatunOtsake()) {
          this.otsikko = this.annaKorvatunOtsake()
        }
        if (this.naytaTavallinenOtsake()) {
          this.otsikko = 'Lasku ' + this._laskuSharedService.annaMuotoiltuLaskunumero(this.juurilasku, this.kasiteltava)
        }
        this._changeDetectorRef.markForCheck()

        if (this.kasiteltava.tilastot?.kirjanpitaja) {
          const kaikkiKipit = await firstValueFrom(this._kirjanpitajaService.kirjanpitajienNimitiedotObservable)
          const kirjanpitaja = kaikkiKipit?.find(k => k.avain === this.kasiteltava.tilastot.kirjanpitaja)
          if (!this.tilastotiedotObservable.value.nimi) {
            this.tilastotiedotObservable.value.nimi = kirjanpitaja ? kirjanpitaja.etunimi + ' ' + kirjanpitaja.sukunimi : ''
            this.tilastotiedotObservable.next(this.tilastotiedotObservable.value)
          }
        }

        if (this.kasiteltava.tilastot?.tiimi) {
          const kaikkiTiimit = await this._kirjanpitajanTiimiService.annaKaikkiTiimit()
          const tiimi = kaikkiTiimit?.find(t => t.avain === this.kasiteltava.tilastot.tiimi)
          if (!this.tilastotiedotObservable.value.tiimi) {
            this.tilastotiedotObservable.value.tiimi = tiimi ? tiimi.nimi : ''
            this.tilastotiedotObservable.next(this.tilastotiedotObservable.value)
          }
        }

        if (data.data.tulosta) {
          setTimeout(() => {
            this.lataaLasku()
            this._changeDetectorRef.markForCheck()
          }, 25)
        }
      })

    const kipienJaKayttajienNimetMapObservable = combineLatest([this._asiakasService.nykyisenAsiakkaanKayttajatObservable, this._kirjanpitajaService.kirjanpitajienNimitiedotObservable]).pipe(
      map(([kayttajat, kipit]) => {
        const kayttajienNimetMap: Map<string, string> = new Map()
        for (const kayttaja of kayttajat) {
          kayttajienNimetMap.set(kayttaja.avain, kayttaja.etunimi + ' ' + kayttaja.sukunimi)
        }
        for (const kipi of kipit) {
          kayttajienNimetMap.set(kipi.avain, kipi.etunimi + ' ' + kipi.sukunimi)
          kayttajienNimetMap.set(kipi.lemonaidUid, kipi.etunimi + ' ' + kipi.sukunimi)
        }
        return kayttajienNimetMap
      }),
    )

    // Lataa lokitiedot
    const lokitiedotFirestorestaObservable = this._route.data.pipe(
      switchMap((routeData: { data: LaskuKatseleComponentData }) => {
        if (routeData && routeData.data && routeData.data.juurilasku && routeData.data.juurilasku.avain) {
          return this._laskuService.getLaskuToimintalokiObservable(routeData.data.juurilasku.avain)
        } else {
          return of<LaskuToimintaloki[]>([])
        }
      }),
    )

    this.lokitiedotObservable = combineLatest([lokitiedotFirestorestaObservable, kipienJaKayttajienNimetMapObservable]).pipe(
      takeUntil(this.ngUnsubscribe),
      map(([kaikkiLokit, kipienJaKayttajienNimetMap]) => {
        const tyyppi = this.kasiteltava.nrotyyppi
        const lokit: KasiteltyLokitieto[] = []
        if (tyyppi === LaskunumeroTyyppi.HYVITYS || tyyppi === LaskunumeroTyyppi.MUISTUTUS) {
          for (const loki of kaikkiLokit) {
            if (loki.lasku_uid === this.kasiteltava.avain) {
              const parametrit = this.annaLokiparametrit(loki, kipienJaKayttajienNimetMap)
              const ending = loki.toiminto === LaskunToimintalokiTyyppi.TILASTO_UPDATE ? 'lemonator' : ''
              const viesti = this._lemonTranslationService.lokalisoi('lasku.tapahtumaloki.tyyppi' + loki.toiminto + ending, parametrit)
              lokit.push({
                loki: loki,
                parametrit: parametrit,
                tekija: (kipienJaKayttajienNimetMap.get(loki.emuloija_uid ?? loki.tekija_uid) || 'Tuntematon tekijä') + ' (' + (loki.emuloija_uid ?? loki.tekija_uid) + ')',
                viesti: viesti,
              })
              if (lokit.length === 1) {
                loki.toiminto = LaskunToimintalokiTyyppi.LUOTU
              }
            }
          }
        } else {
          const sallitutAvaimet = this._laskuSharedService.annaLaskuryppaanPaalaskunEriVersiot(this.juurilasku).map(versio => {
            return versio.avain
          })
          for (const loki of kaikkiLokit) {
            if (sallitutAvaimet.includes(loki.lasku_uid)) {
              const parametrit = this.annaLokiparametrit(loki, kipienJaKayttajienNimetMap)
              const ending = loki.toiminto === LaskunToimintalokiTyyppi.TILASTO_UPDATE ? 'lemonator' : ''
              const viesti = this._lemonTranslationService.lokalisoi('lasku.tapahtumaloki.tyyppi' + loki.toiminto + ending, parametrit)
              lokit.push({
                loki: loki,
                parametrit: parametrit,
                tekija: (kipienJaKayttajienNimetMap.get(loki.emuloija_uid ?? loki.tekija_uid) || 'Tuntematon tekijä') + ' (' + (loki.emuloija_uid ?? loki.tekija_uid) + ')',
                viesti: viesti,
              })
            }
          }
        }
        return lokit
      }),
      lemonShare(),
    )

    this.lokitietojaLadataanObservable = this.lokitiedotObservable.pipe(
      map(() => {
        return false
      }),
      startWith(true),
    )

    // Lataa reskontratiedot
    const reskontraObservable = this._route.data.pipe(
      switchMap((data: { data: LaskuKatseleComponentData }) => {
        if (data && data.data && data.data.juurilasku && data.data.juurilasku.avain) {
          return this._laskuService.getLaskuReskontraObservable(data.data.juurilasku.avain)
        } else {
          return of<LaskuReskontra[]>([])
        }
      }),
      lemonShare(),
    )

    this.reskontraSummaObservable = reskontraObservable.pipe(
      map(reskontra => {
        const maksettu = this._reskontraService.annaReskontranSumma(reskontra)
        if (this.juurilasku && this.juurilasku.tila === LaskunTila.maksettu) {
          return this._currencyService.muutaBigDecimalRahaksi(maksettu)
        } else if (this.kasiteltava) {
          return this._currencyService.muutaBigDecimalRahaksi(maksettu.minus(this.kasiteltava.summa))
        }
        return 0
      }),
    )

    this.reskontraaLadataanObservable = reskontraObservable.pipe(
      map(() => {
        return false
      }),
      startWith(true),
    )

    this.reskontraLokiObservable = combineLatest([reskontraObservable, this._lemonTranslationService.currentLanguageObservable, kipienJaKayttajienNimetMapObservable]).pipe(
      map(([reskontrat, kieli, kayttajienNimetMap]) => {
        return reskontrat
          .sort((a, b) => {
            return this._dateService.compareTimestamps(a.luotu, '<', b.luotu) ? -1 : 1
          })
          .map(r => {
            return { data: r, teksti: this._annaReskontraLokinSelite(r, this.kasiteltava?.valuutta, kayttajienNimetMap, kieli) }
          })
      }),
    )

    this.tilastoKirjanpitajaTaiSuperObservable = this._kirjautunutKayttajaService.kirjanpitajanTiedotObservable.pipe(
      map(kirjanpitaja => {
        if (kirjanpitaja && this.kasiteltava?.tilastot) {
          return kirjanpitaja.uid === this.kasiteltava.tilastot.kirjanpitaja || kirjanpitaja.rooli === KirjanpitajanRooli.SUPER
        }
        return false
      }),
    )

    this.kirjanpitajienNimiTiedotObservable = this._kirjanpitajaService.kirjanpitajienNimitiedotObservable.pipe(
      map(kirjanpitajat => kirjanpitajat.sort((a, b) => a.etunimi.localeCompare(b.etunimi) || a.sukunimi.localeCompare(b.sukunimi))),
    )

    this.tiimitJaNimetObservable = arvonMuutokset(this.tilastoLomake.kirjanpitaja, { injector: this._injector }).pipe(
      switchMap(async kirjanpitaja => {
        if (!kirjanpitaja) {
          return []
        }
        const kaikkiTiimitPaivalle = await this._kirjanpitajanTiimiService.annaKaikkiTiimit()
        const valittavissaOlevat = kaikkiTiimitPaivalle.filter(
          t => t.esihenkilot.some(e => e.kirjanpitajaAvain === kirjanpitaja.avain) || t.jasenet.some(j => j.kirjanpitajaAvain === kirjanpitaja.avain),
        )
        valittavissaOlevat.sort((a, b) => {
          const aep = a.esihenkilot.some(e => e.kirjanpitajaAvain === kirjanpitaja.avain && e.primary)
          const ajp = a.jasenet.some(j => j.kirjanpitajaAvain === kirjanpitaja.avain && j.primary)
          if (aep || ajp) {
            return -1
          }
          const bep = a.esihenkilot.some(e => e.kirjanpitajaAvain === kirjanpitaja.avain && e.primary)
          const bjp = a.jasenet.some(j => j.kirjanpitajaAvain === kirjanpitaja.avain && j.primary)
          if (bep || bjp) {
            return 1
          }
          return a.nimi.localeCompare(b.nimi)
        })
        asetaArvo(this.tilastoLomake.tiimi, valittavissaOlevat.length > 0 ? valittavissaOlevat[0] : null)
        return valittavissaOlevat.map(tiimi => {
          const voimassaEsihenkilona = tiimi.esihenkilot
            .filter(e => e.kirjanpitajaAvain === kirjanpitaja.avain)
            .map(e => {
              let voimassa = ''
              if (e.alku && e.alku !== 100101) {
                voimassa = this._dateService.muotoileNumberPaiva(e.alku, 'fi') + ' - '
              }
              if (e.loppu) {
                if (!voimassa) {
                  voimassa = ' - '
                }
                voimassa += this._dateService.muotoileNumberPaiva(e.loppu, 'fi')
              }
              return voimassa
            })
            .join(',')
          const voimassaTyontekijana = tiimi.jasenet
            .filter(j => j.kirjanpitajaAvain === kirjanpitaja.avain)
            .map(j => {
              let voimassa = ''
              if (j.alku && j.alku !== 100101) {
                voimassa = this._dateService.muotoileNumberPaiva(j.alku, 'fi') + ' - '
              }
              if (j.loppu) {
                if (!voimassa) {
                  voimassa = ' - '
                }
                voimassa += this._dateService.muotoileNumberPaiva(j.loppu, 'fi')
              }
              return voimassa
            })
            .join(',')
          const results = []
          if (voimassaEsihenkilona) {
            results.push('Esihenkilö: ' + voimassaEsihenkilona)
          }
          if (voimassaTyontekijana) {
            results.push('Työntekijä: ' + voimassaTyontekijana)
          }
          const voimassaolo = results.length ? ' (' + results.join(',') + ')' : ''
          return {
            tiimi: tiimi,
            nimi: tiimi.nimi + voimassaolo,
          }
        })
      }),
    )

    this.naytaKampanjahyvitysInfoObservable = this._route.data.pipe(
      map((data: { data: LaskuKatseleComponentData }) => {
        const tuotteet = data?.data?.kasiteltava?.tuotteet
        if (!tuotteet) {
          return false
        }
        const kampanjahyvitysRivit = tuotteet.filter(tuote => tuote.tuote.avain === LaskutettavatPalvelut.KAMPANJAHYVITYS)
        const muutRivit = tuotteet.filter(tuote => tuote.tuote.avain !== LaskutettavatPalvelut.KAMPANJAHYVITYS)

        return kampanjahyvitysRivit.length > 0 && muutRivit.length > 0
      }),
    )

    window.scroll({ top: 0, left: 0 })
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next()
    this.ngUnsubscribe.complete()
  }

  valittajaDisplayFn(operaattori?: Verkkolaskuoperaattori): string | undefined {
    if (operaattori && operaattori.nimi) {
      return operaattori.nimi + ' (' + operaattori.tunnus + ')'
    }
    if (operaattori instanceof String || typeof operaattori === 'string') {
      return operaattori as any as string
    }
    return undefined
  }

  compareTiimit(tiimi1: KirjanpitajanTiimi, tiimi2: KirjanpitajanTiimi) {
    return tiimi1?.avain === tiimi2?.avain
  }

  compareKirjanpitajat(kp1: KirjanpitajanNimitiedot, kp2: KirjanpitajanNimitiedot) {
    return kp1?.avain === kp2?.avain
  }

  lataaLiitetiedosto(liitetiedosto: LaskunLiitetiedosto) {
    this._ladataanService.aloitaLataaminen()
    const liitetiedostoUri = this._laskuService.getLiitetiedostoUri(liitetiedosto)
    const url = '/laskuLataaLiite?a=' + encodeURIComponent('/api/1/laskut/lataaLiitetiedosto/' + liitetiedostoUri) + '&time=' + encodeURIComponent(new Date().getTime())
    return this._httpService
      .getBinary(url, LEMONATOR_CF_API)
      .then(result => {
        this._ladataanService.lopetaLataaminen()
        this._fileSaverService.saveAs(result, liitetiedosto.nimi)
      })
      .catch(err => {
        this._ladataanService.lopetaLataaminen()
        this._errorHandler.handleError(err)
      })
  }

  lataaLasku() {
    console.log(this.kasiteltava.sahkoinen)
    if ((this.kasiteltava.print && this.kasiteltava.print.done) || (this.kasiteltava.email && this.kasiteltava.email.done) || (this.kasiteltava.sahkoinen && this.kasiteltava.sahkoinen.done)) {
      this._ladataanService.aloitaLataaminen()
      const pdfUri = this._laskuService.getPdfUrl(this.kasiteltava)
      const url = '/api/1/laskut/lataaPdf/' + pdfUri
      return this._httpService
        .getBinary('/laskuLataaPdf?a=' + encodeURIComponent(url) + '&time=' + encodeURIComponent(new Date().getTime()), LEMONATOR_CF_API)
        .then(result => {
          const nimi = this._laskuService.annaPdfTiedostonNimi(this.juurilasku, this.kasiteltava, this._lemonTranslationService.nykyinenKieli)
          this._ladataanService.lopetaLataaminen()
          this._fileSaverService.saveAs(result, nimi)
        })
        .catch(err => {
          this._ladataanService.lopetaLataaminen()
          this._errorHandler.handleError(err)
        })
    } else {
      const data: LaskuLataaDialogData = {
        juurilasku: this.juurilasku,
        kasiteltava: this.kasiteltava,
      }
      const dialogRef = this._dialog.open(LaskuLataaDialog, { data: data, panelClass: 'ilman-paddingia' })
    }
  }

  async vaihdaLaskunTilastoLemonator() {
    if (this._buttonPushInflight) {
      return
    }
    this._buttonPushInflight = true

    const tilastoKirjanpitaja = this.tilastoLomake.kirjanpitaja().value()
    const tilastoTiimi = this.tilastoLomake.tiimi().value()
    const tallentavaKirjanpitaja = await firstValueFrom(this._kirjanpitajaService.kirjautuneenKayttajanKirjanpitajaObservable)

    if (!tilastoKirjanpitaja && !tilastoTiimi) {
      return
    }

    try {
      this._ladataanService.aloitaLataaminen()

      const uudetTilastotiedot: TilastoTiedot = {
        kirjanpitaja: tilastoKirjanpitaja.avain,
        tiimi: tilastoTiimi.avain,
      }

      await this._laskuService.vaihdaLaskunTilastoLemonator(uudetTilastotiedot, this.juurilasku, this.kasiteltava, tallentavaKirjanpitaja.avain)

      this.tilastotiedotObservable.value.nimi = tilastoKirjanpitaja.etunimi + ' ' + tilastoKirjanpitaja.sukunimi
      this.tilastotiedotObservable.value.tiimi = tilastoTiimi.nimi
      this.tilastotiedotObservable.next(this.tilastotiedotObservable.value)

      this._snackbar.open('Tilastotiedot tallennettiin onnistuneesti.', 'OK', { duration: 10000, verticalPosition: 'top' })
    } catch (error) {
      this._errorHandler.handleError(error)
      this._snackbar.open(this._lemonTranslationService.lokalisoi('yleiset.tuntematon-virhe'), 'OK', { duration: 10000 })
    } finally {
      this._ladataanService.lopetaLataaminen()
      this._buttonPushInflight = false
    }
  }

  annaKorvatunOtsake(): string {
    const korvaava = this._laskuSharedService.annaKorvaavaLasku(this.juurilasku, this.kasiteltava)
    const korvaavanNumero =
      korvaava.nrotyyppi === LaskunumeroTyyppi.MUISTUTUS ? this._laskuSharedService.annaMuotoiltuLaskunumero(this.juurilasku, korvaava) : this._laskuSharedService.annaLaskunumero(korvaava)
    return `Laskusta on lähtenyt ${this._annaLaskutyypinNimi(korvaava.nrotyyppi)} ${korvaavanNumero}`.trim()
  }

  private _annaLaskutyypinNimi(nrotyyppi: LaskunumeroTyyppi): string {
    if (nrotyyppi === LaskunumeroTyyppi.MUISTUTUS) {
      return 'maksumuistutus'
    }
    if (nrotyyppi === LaskunumeroTyyppi.HYVITYS) {
      return 'hyvityslasku'
    }
    if (nrotyyppi === LaskunumeroTyyppi.KORJAUS) {
      return 'korjauslasku'
    }
    return 'uusi lasku'
  }

  naytaKorvatunOtsake(): boolean {
    return this._laskuSharedService.annaKorvaavaLasku(this.juurilasku, this.kasiteltava) && true
  }

  naytaTavallinenOtsake(): boolean {
    return !this.naytaKorvatunOtsake()
  }

  naytaSumma(): boolean {
    return !this.naytaKorvatunOtsake()
  }

  naytaMaksuaikaa(): boolean {
    return this.juurilasku && this.kasiteltava && (this.juurilasku.tila === 'a' || this.juurilasku.tila === 'e') && !this.naytaKorvatunOtsake()
  }

  annaMaksuaikaa(): number {
    if (this.kasiteltava && this.kasiteltava.erapvm) {
      return this._dateService.paiviaValissaTimestamp(this.kasiteltava.erapvm, this._timestampService.now())
    }
    return 0
  }

  private _annaReskontraLokinSelite(reskontra: LaskuReskontra, valutta: string, kayttajienNimetMap: Map<string, string>, kieli: TuettuKieli): string {
    const kayttajanNimi = reskontra.tekijaUid === 'lemonator_system' ? 'lemonator_system' : (kayttajienNimetMap.get(reskontra.tekijaUid) ?? 'Tuntematon käyttäjä') + '(' + reskontra.tekijaUid + ')'
    if (reskontra.siirrettyTilitapahtumasta) {
      const maksajaPart = reskontra.maksaja ? ' (' + reskontra.maksaja + ')' : ''
      return `${this._dateService.muotoile(reskontra.luotu.toDate(), 'dd.MM.yyyy HH:mm:ss')}, ${kayttajanNimi}: ${this._lemonTranslationService.lokalisoi('lasku.katsele-muokattava-dialog.automaattimerkinta-tilitapahtumasta')}: ${this._currencyService.formatoiRaha(reskontra.suoritus, valutta.toLowerCase(), kieli)}${maksajaPart}`
    }
    if (reskontra.siirrettyLaskuun) {
      return `${this._dateService.muotoile(reskontra.luotu.toDate(), 'dd.MM.yyyy HH:mm:ss')}, ${kayttajanNimi} Siirretty laskuun nro ${reskontra.siirrettyLaskuunNro}: ${this._currencyService.formatoiRaha(reskontra.suoritus, valutta.toLowerCase(), kieli)}`
    }
    if (reskontra.siirrettyLaskusta) {
      return `${this._dateService.muotoile(reskontra.luotu.toDate(), 'dd.MM.yyyy HH:mm:ss')}, ${kayttajanNimi} Siirretty laskusta nro ${reskontra.siirrettyLaskustaNro}: ${this._currencyService.formatoiRaha(reskontra.suoritus, valutta.toLowerCase(), kieli)}`
    }
    return `${this._dateService.muotoile(reskontra.luotu.toDate(), 'dd.MM.yyyy HH:mm:ss')}, ${kayttajanNimi}: ${this._currencyService.formatoiRaha(reskontra.suoritus, valutta.toLowerCase(), kieli)}`
  }

  // poistaLuonnos() {
  //   this.laskuService.poistaLuonnos(this.juurilasku, this.kasiteltava).then(() => {
  //     this.router.navigate(['/laskutus/laskut'])
  //   })
  // }

  // merkitseLuottotappioksi() {
  //   this.laskuService.merkitseLuottotappioksi(this.juurilasku, this.kasiteltava)
  // }

  // peruutaLuottotappiomerkinta() {
  //   this.laskuService.peruutaLuottotappiomerkinta(this.juurilasku, this.kasiteltava)
  // }

  // merkitseOsittainMaksetuksi() {
  //   const data: LaskuMerkitseOsittainMaksetuksiDialogData = {
  //     juurilasku: this.juurilasku,
  //     kasiteltava: this.kasiteltava
  //   }

  //   const settings: MatDialogConfig = {
  //     'data': data,
  //     panelClass: 'ilman-paddingia'
  //     // ,
  //     // maxWidth: '1600px',
  //     // maxHeight: korkeus,
  //     // width: leveys
  //   }
  //   const dialogRef = this.dialog.open(LaskuMerkitseOsittainMaksetuksiDialog, settings)
  // }

  // muokkaa() {

  //   // console.log(this.juurilasku.tila, this.juurilasku.tila === LaskunTila.luonnos)

  //   if (this.kasiteltava.print && this.kasiteltava.print.start) {

  //     const data: LaskuMuokkaaTulostettuaDialogData = {
  //       juurilasku: this.juurilasku,
  //       kasiteltava: this.kasiteltava,
  //       asetukset: this.asetukset
  //     }
  //     const settings: MatDialogConfig = {
  //       data: data
  //     }
  //     this.dialog.open(LaskuMuokkaaTulostettuaDialog, settings)

  //   } else {

  //     const data: LaskuComponentExistingData = {
  //       juurilasku: this.juurilasku,
  //       asetukset: this.asetukset
  //     }
  //     this.laskuComponentDataResolve.asetaOlemassaolevaData(data)

  //     if (this.juurilasku.tila === LaskunTila.luonnos) {
  //       this.router.navigate(['/laskutus/laskut/', this.juurilasku.avain, this.kasiteltava.avain])
  //     } else {
  //       this.router.navigate(['/laskutus/laskut/', this.juurilasku.avain, UUDEN_LASKUN_AVAIN])
  //     }

  //   }

  // }

  onkoLataaLaskuNakyvissa(): boolean {
    return this.kasiteltava && (!!this.kasiteltava.print?.done || !!this.kasiteltava.email?.done || !!this.kasiteltava.sahkoinen?.done)
  }

  onkoHyvitysNakyvissa(): boolean {
    return (
      this.kasiteltava &&
      this.kasiteltava.nrotyyppi === LaskunumeroTyyppi.TAVALLINEN &&
      (this.juurilasku.tila === LaskunTila.avoin ||
        this.juurilasku.tila === LaskunTila.maksettuLiikaa ||
        this.juurilasku.tila === LaskunTila.maksettu ||
        this.juurilasku.tila === LaskunTila.eraantynyt)
    )
  }

  // onkoPoistoLuonnosNakyvissa(): boolean {
  //   return this.kasiteltava &&
  //     this.juurilasku.tila === LaskunTila.luonnos
  // }

  // onkoPeruutaLuottotappioMerkintaNakyvissa(): boolean {
  //   return this.kasiteltava &&
  //     this.juurilasku.tila === LaskunTila.luottotappio
  // }

  // onkoMerkitseOsittainMaksetuksiNakyvissa(): boolean {
  //   return this.kasiteltava &&
  //     this.juurilasku.tila !== LaskunTila.luottotappio &&
  //     this.laskuSharedService.onkoKasiteltavaMuuKuinLuonnos(this.juurilasku, this.kasiteltava)
  // }

  // onkoMerkitseLuottotappioksiNakyvissa(): boolean {
  //   return this.kasiteltava &&
  //     this.juurilasku.tila !== LaskunTila.luottotappio &&
  //     this.juurilasku.tila !== LaskunTila.maksettu &&
  //     this.juurilasku.tila !== LaskunTila.maksettuLiikaa &&
  //     this.laskuSharedService.onkoKasiteltavaMuuKuinLuonnos(this.juurilasku, this.kasiteltava)
  // }

  // onkoMaksumuistutusNakyvissa(): boolean {
  //   return this.kasiteltava &&
  //     this.juurilasku.tila !== LaskunTila.luottotappio &&
  //     this.juurilasku.tila !== LaskunTila.maksettu &&
  //     this.juurilasku.tila !== LaskunTila.maksettuLiikaa &&
  //     this.laskuSharedService.onkoKasiteltavaMuuKuinLuonnos(this.juurilasku, this.kasiteltava)
  // }

  async hyvita() {
    if (this._buttonPushInflight) {
      return
    }
    if (!this.asiakas || !this.asiakas.avain) {
      return
    }
    this._buttonPushInflight = true

    try {
      // Vaihda jatkuvan laskutuksen tuotteet korjaustuotteiksi
      const muokattuJuurilasku = this._laskuKopioija.copyLasku(this.juurilasku)
      this._laskuKopioija.korvaaJatkuvatTuotteetKorjaustuotteilla(muokattuJuurilasku)

      const data: LaskuComponentExistingData = {
        juurilasku: muokattuJuurilasku,
        asetukset: this.asetukset,
      }
      this._laskuComponentDataResolve.asetaOlemassaolevaData(data)

      await this._router.navigate(['asiakkaat', this.asiakas.avain, 'laskutus', 'luo', muokattuJuurilasku.avain, 'hyvita'])
      this._vasenValikkoService.paivitaAuki(true)
    } catch (error) {
      console.error('Navigointi epäonnistui:', error)
    } finally {
      this._buttonPushInflight = false
    }
  }

  private naytaMuokkausnappulat(): boolean {
    if (!this.kasiteltava) {
      return false
    }
    if (!this.kasiteltava.avain) {
      return false
    }
    const liittyvatLaskut = this._laskuSharedService.annaLiittyvatLaskut(this.juurilasku)
    if (!liittyvatLaskut.length) {
      return this._laskuSharedService.onkoKasiteltavaMuuKuinMitatoity(this.juurilasku, this.kasiteltava)
    }
    if (liittyvatLaskut[liittyvatLaskut.length - 1].avain === this.kasiteltava.avain) {
      return this._laskuSharedService.onkoKasiteltavaMuuKuinMitatoity(this.juurilasku, this.kasiteltava)
    }
    return false
  }

  naytaEpaonnistunutSahkoinen(): boolean {
    if (!this.kasiteltava || !this.kasiteltava.sahkoinen) {
      return false
    }

    if (this.kasiteltava.sahkoinen.status !== LaskuSahkoinenLahetysStatusKoodi.LAHETETTY_VASTAANOTTAJALLE && this.kasiteltava.sahkoinen.status !== LaskuSahkoinenLahetysStatusKoodi.PROSESSOIDAAN) {
      return true
    }

    return false
  }

  naytaEpaonnistunutSposti(): boolean {
    if (!this.naytaMuokkausnappulat()) {
      return false
    }

    if (!this.kasiteltava || !this.kasiteltava.email || !this.kasiteltava.email.vastaanottajat) {
      return false
    }

    for (const vastaanottaja of this.kasiteltava.email.vastaanottajat) {
      if (vastaanottaja.status === EmailLahetysStatusKoodi.LAHETYS_EPAONNISTUI) {
        return true
      }
    }

    return false
  }

  naytaKokoVirhe(vastaanottaja: EmailLahetysStatus) {
    const dialogRef = this._dialog.open(LaskuKokoVirheDialog, { data: vastaanottaja, panelClass: 'ilman-paddingia' })
  }

  lahetaEmailUudelleen(vastaanottaja: KorjattavaEmailLahetysStatus) {
    combineLatest([this._kirjanpitajaService.kirjautuneenKayttajanKirjanpitajaObservable, this._asiakasService.nykyinenAsiakasObservable])
      .pipe(take(1))
      .subscribe(([kirjanpitaja, asiakas]) => {
        if (kirjanpitaja && asiakas) {
          this._laskuService.lahetaEmailUudelleen(this.juurilasku, this.kasiteltava, asiakas, kirjanpitaja, vastaanottaja, vastaanottaja.vanhaEmail).catch(error => {
            this._errorHandler.handleError(error)
          })
        }
      })
  }

  lahetaSahkoinenUudelleen() {
    if (!this.sahkoinenLomake().valid()) {
      this._validationService.merkitseKokoLomakeKosketuksi(this.sahkoinenLomake)
      return
    }
    combineLatest([this._kirjanpitajaService.kirjautuneenKayttajanKirjanpitajaObservable, this._asiakasService.nykyinenAsiakasObservable])
      .pipe(take(1))
      .subscribe(([kirjanpitaja, asiakas]) => {
        if (kirjanpitaja && asiakas) {
          this._laskuService.lahetaSahkoinenUudelleen(this.juurilasku, this.kasiteltava, asiakas, kirjanpitaja).catch(error => {
            this._errorHandler.handleError(error)
          })
        }
      })
  }

  private annaLokiparametrit(loki: LaskuToimintaloki, kipienJaKayttajienNimetMap: Map<string, string>): any {
    const paluuarvo: any = {}

    paluuarvo.lasku_nro = this._laskuSharedService.annaMuotoiltuLaskunumero(this.juurilasku, this.kasiteltava)

    if (loki.toiminto === LaskunToimintalokiTyyppi.LAHETETTY_SPOSTI || loki.toiminto === LaskunToimintalokiTyyppi.LAHETETTY_MAKSUMUISTUTUS_SPOSTI) {
      if (loki.parametrit) {
        const parametrit = loki.parametrit as LaskunToimintalokiSpostiParametrit
        paluuarvo.osoitteet = parametrit.emailit ? parametrit.emailit.join(', ') : ''
      } else {
        paluuarvo.osoitteet = '?'
      }
    } else if (
      loki.toiminto === LaskunToimintalokiTyyppi.LAHETETTY_SAHKOINEN ||
      loki.toiminto === LaskunToimintalokiTyyppi.LAHETETTY_TULOSTA_TOISEN_HYVAKSYNTAJONOON_ODOTTAMAAN_HYVAKSYNTAA ||
      loki.toiminto === LaskunToimintalokiTyyppi.LAHETETTY_SAHKOISESTI_TOISEN_HYVAKSYNTAJONOSTA_HYVAKSYNNAN_JALKEEN
    ) {
      if (loki.parametrit) {
        const parametrit = loki.parametrit as LaskunToimintalokiSahkoinenParametrit
        const tunnus = parametrit.osoite && parametrit.osoite.sahkoinenValittaja ? parametrit.osoite.sahkoinenValittaja : ''
        const kaytettevaTunnus = (tunnus as any).tunnus ? (tunnus as any).tunnus : tunnus
        // console.log(kaytettevaTunnus)
        const nimi = this._sahkoisenLaskunValittajaService.annaValittajanNimi(kaytettevaTunnus)
        const valittaja = kaytettevaTunnus + (nimi ? ' (' + nimi + ')' : '')
        // console.log(valittaja)
        paluuarvo.sahkoinenOsoite = parametrit.osoite && parametrit.osoite.sahkoinenOsoite ? parametrit.osoite.sahkoinenOsoite : ''
        paluuarvo.sahkoinenValittaja = valittaja
      } else {
        paluuarvo.sahkoinenOsoite = '?'
        paluuarvo.sahkoinenValittaja = '?'
      }
    } else if (loki.toiminto === LaskunToimintalokiTyyppi.TILASTO_UPDATE) {
      if (loki.parametrit) {
        const parametrit = loki.parametrit as LaskunToimintalokiTilastoParametrit
        paluuarvo.vanhaTiimi = parametrit.vanhaTiimi ?? '?'
        paluuarvo.uusiTiimi = parametrit.uusiTiimi ?? '?'
        paluuarvo.vanhaKirjanpitaja = (kipienJaKayttajienNimetMap.get(parametrit.vanhaKirjanpitaja) ?? '?') + ' (' + (parametrit.vanhaKirjanpitaja ?? '?') + ')'
        paluuarvo.uusiKirjanpitaja = (kipienJaKayttajienNimetMap.get(parametrit.uusiKirjanpitaja) ?? '?') + ' (' + (parametrit.uusiKirjanpitaja ?? '?') + ')'
      } else {
        paluuarvo.vanhaTiimi = '?'
        paluuarvo.uusiTiimi = '?'
        paluuarvo.vanhaKirjanpitaja = '?'
        paluuarvo.uusiKirjanpitaja = '?'
      }
    }

    return paluuarvo
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }

  naytaLoki(loki: KasiteltyLokitieto) {
    this._dialog.open(LaskuReskontraTiedotDialog, { data: loki })
  }

  naytaMerkinta(merkinta: LaskuReskontra) {
    this._dialog.open(LaskuReskontraTiedotDialog, { data: merkinta })
  }

  // peruuta() {
  //   this.router.navigate(['asiakkaat', '/laskutus/laskut'])
  // }

  vertaaYhtaSuuret(yksi: number, kaksi: number): boolean {
    return this._currencyService.roundHalfUp(yksi, 2) === this._currencyService.roundHalfUp(kaksi, 2)
  }

  vertaaEkaPienempiKuinToka(pienempi: number, suurempi: number): boolean {
    return this._currencyService.roundHalfUp(pienempi, 2) < this._currencyService.roundHalfUp(suurempi, 2)
  }

  async paivitaTilaJaIndeksoiUudelleen() {
    if (this._buttonPushInflight) {
      return
    }
    this._buttonPushInflight = true
    this._ladataanService.aloitaLataaminen()

    const kirjanpitaja = await firstValueFrom(this._kirjanpitajaService.kirjautuneenKayttajanKirjanpitajaObservable)

    if (!kirjanpitaja) {
      this._ladataanService.lopetaLataaminen()
      this._buttonPushInflight = false
      return
    }

    try {
      const asiakkaanAvaintiedot: AsiakkaanAvainTiedot = {
        asiakasId: this._laskuService.LEMONTREE_ASIAKAS_ID,
        avain: this._laskuService.LEMONTREE_ASIAKAS_AVAIN,
      }
      await this._laskuService.paivitaTilaJaIndeksoiUudelleen(this.juurilasku, asiakkaanAvaintiedot, kirjanpitaja)
    } catch (error) {
      this._errorHandler.handleError(error)
    } finally {
      this._ladataanService.lopetaLataaminen()
      this._buttonPushInflight = false
    }
  }

  async laskeReskontraUudelleen() {
    if (this._buttonPushInflight) {
      return
    }
    this._buttonPushInflight = true
    this._ladataanService.aloitaLataaminen()

    const kirjanpitaja = await firstValueFrom(this._kirjanpitajaService.kirjautuneenKayttajanKirjanpitajaObservable)

    if (!kirjanpitaja) {
      this._ladataanService.lopetaLataaminen()
      this._buttonPushInflight = false
      return
    }

    try {
      const asiakkaanAvaintiedot: AsiakkaanAvainTiedot = {
        asiakasId: this._laskuService.LEMONTREE_ASIAKAS_ID,
        avain: this._laskuService.LEMONTREE_ASIAKAS_AVAIN,
      }
      await this._laskuService.laskeReskontraUudelleen(this.juurilasku, asiakkaanAvaintiedot, kirjanpitaja)
    } catch (error) {
      this._errorHandler.handleError(error)
    } finally {
      this._ladataanService.lopetaLataaminen()
      this._buttonPushInflight = false
    }
  }
}
